import React, { useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Film, Play, Sparkles, X, Loader2, Scissors, ArrowUpRight } from 'lucide-react';
import { openCheckout } from '../../utils/openCheckout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Owners see "15 sec max" in the UI, but we allow up to 16s to avoid rejecting
// videos that are a fraction over (e.g. a 15.6s export).
const MAX_VIDEO_DURATION_SEC = 15;
const MAX_VIDEO_DURATION_HARD_SEC = 16;

// Hard size cap — videos above this are rejected client-side before any
// upload attempt. Keeping it small avoids backend memory pressure and
// lets the upload complete inside a single ingress request.
const MAX_VIDEO_SIZE_BYTES = 50 * 1024 * 1024; // 50 MB
const MAX_VIDEO_SIZE_LABEL = '50MB';

// External compressor the user can self-serve to shrink an over-size
// clip. ONLY surfaced when a pick fails the size check — never for
// duration/codec rejections, because compressing wouldn't help there.
const COMPRESS_URL = 'https://videocompress.ai';

const resolveVideoSrc = (url) => {
  if (!url) return '';
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return `${BACKEND_URL}${url}`;
};

/**
 * Probe a local video file in the browser to make sure it's <= 15 seconds.
 * Resolves with the duration in seconds. Falls back to 0 (unknown) on
 * codec-metadata errors so the file still proceeds to the server.
 */
function readVideoDuration(file) {
  return new Promise((resolve) => {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    video.muted = true;
    video.playsInline = true;

    let settled = false;
    const cleanup = () => {
      settled = true;
      URL.revokeObjectURL(objectUrl);
    };

    video.onloadedmetadata = () => {
      if (settled) return;
      cleanup();
      resolve(video.duration);
    };
    video.onerror = () => {
      if (settled) return;
      cleanup();
      resolve(0);
    };
    setTimeout(() => {
      if (settled) return;
      cleanup();
      resolve(video.duration && isFinite(video.duration) ? video.duration : 0);
    }, 5000);

    video.src = objectUrl;
    video.load();
  });
}

/**
 * Intro video uploader card used in venue onboarding (Step 7) and the
 * venue settings → photos page.
 *
 * Simple single-shot upload: 50 MB hard cap, 15 s duration cap, POST
 * straight to /api/venue/upload-video.
 *
 * When a user picks a clip that's too large, we surface an inline
 * "Compress your video" CTA that opens an external compressor
 * (videocompress.ai) in Chrome Custom Tabs / SFSafariViewController so
 * they can shrink the file and retry without leaving the Allure app.
 * The CTA is ONLY shown for size-based rejections, per spec — not for
 * duration/codec problems, because compressing wouldn't help there.
 */
export function IntroVideoUploader({
  value,
  onChange,
  testId = 'venue-intro-video',
  getAuthHeaders,
  helperText,
}) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [oversized, setOversized] = useState(null); // { sizeMb: '72.4' } | null

  // Content-type on native Capacitor file picks often arrives as
  // "application/octet-stream" (Android) with the correct extension
  // nonetheless. Fall through to extension-based detection.
  const looksLikeVideo = (file) => {
    if (!file) return false;
    if (file.type && file.type.startsWith('video/')) return true;
    const name = (file.name || '').toLowerCase();
    return /\.(mp4|mov|m4v|webm)$/.test(name);
  };

  const handlePick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!looksLikeVideo(file)) {
      toast.error('Please choose a video file (MP4, MOV or WebM).');
      event.target.value = '';
      return;
    }

    if (file.size > MAX_VIDEO_SIZE_BYTES) {
      const sizeMb = (file.size / 1024 / 1024).toFixed(1);
      // Keep a toast for the "something went wrong" moment, AND show
      // the persistent inline CTA so the user can act on it without
      // the toast timing out.
      toast.error(`Video is too large (max ${MAX_VIDEO_SIZE_LABEL}). Yours is ${sizeMb}MB.`);
      setOversized({ sizeMb });
      event.target.value = '';
      return;
    }

    const duration = await readVideoDuration(file);
    if (duration > MAX_VIDEO_DURATION_HARD_SEC) {
      toast.error(`Video must be ${MAX_VIDEO_DURATION_SEC} seconds or shorter. Yours is ${duration.toFixed(1)}s.`);
      event.target.value = '';
      return;
    }

    // A valid pick — clear any lingering oversized state from a
    // previous failed attempt so the CTA doesn't stick around.
    setOversized(null);

    setUploading(true);
    const toastId = toast.loading('Uploading your intro video…');
    try {
      const headers = typeof getAuthHeaders === 'function' ? getAuthHeaders() : {};
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/venue/upload-video`, formData, {
        headers,
        timeout: 120000,
      });
      onChange?.(res.data.url);
      toast.success('Intro video uploaded', { id: toastId });
    } catch (err) {
      console.error('Video upload failed:', err);
      const serverDetail = err?.response?.data?.detail;
      const fallback = err?.code === 'ECONNABORTED'
        ? 'Upload timed out. Please check your connection and try again.'
        : 'Upload failed. Please try again.';
      toast.error(serverDetail || fallback, { id: toastId });
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  };

  const handleRemove = () => {
    onChange?.('');
    setOversized(null);
    toast.success('Intro video removed');
  };

  const handleOpenCompressor = async () => {
    try {
      await openCheckout(COMPRESS_URL);
    } catch (err) {
      console.error('Failed to open compressor:', err);
      toast.error("Couldn't open the compressor. Please try again.");
    }
  };

  // Inline CTA shown ONLY when a file was rejected for being over 50MB.
  // Rendered above the uploader so the owner's eye lands on the fix
  // before they re-tap the picker.
  const renderOversizedCta = () => {
    if (!oversized) return null;
    return (
      <div
        data-testid={`${testId}-oversized-cta`}
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 10,
          padding: '14px 16px',
          borderRadius: 16,
          background: 'var(--status-warning-surface)',
          border: '1.5px solid var(--status-warning-text)',
          marginBottom: 12,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span
            style={{
              width: 32,
              height: 32,
              flexShrink: 0,
              borderRadius: 10,
              background: 'rgba(255,255,255,0.6)',
              color: 'var(--status-warning-text)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Scissors size={16} strokeWidth={2.4} />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span
              style={{
                display: 'block',
                fontFamily: "'Inria Serif', serif",
                fontSize: 14,
                fontWeight: 700,
                color: 'var(--status-warning-text)',
                lineHeight: 1.3,
              }}
            >
              Your video is {oversized.sizeMb}MB (max {MAX_VIDEO_SIZE_LABEL})
            </span>
            <span
              style={{
                display: 'block',
                marginTop: 2,
                fontFamily: "'Inria Serif', serif",
                fontSize: 12.5,
                color: 'var(--status-warning-text)',
                opacity: 0.9,
                lineHeight: 1.35,
              }}
            >
              Shrink it with a free online compressor, then upload again.
            </span>
          </span>
        </div>
        <button
          type="button"
          onClick={handleOpenCompressor}
          data-testid={`${testId}-oversized-compress`}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            padding: '10px 14px',
            borderRadius: 999,
            background: 'var(--status-warning-text)',
            color: '#FFFFFF',
            border: 'none',
            cursor: 'pointer',
            fontFamily: "'Inria Serif', serif",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: '0.01em',
            boxShadow: '0 6px 16px rgba(20, 16, 50, 0.18)',
          }}
        >
          Compress your video
          <ArrowUpRight size={14} strokeWidth={2.6} />
        </button>
      </div>
    );
  };

  // === uploaded state ===
  if (value) {
    return (
      <>
        {renderOversizedCta()}
        <div
          data-testid={`${testId}-preview`}
          style={{
            position: 'relative',
            width: '100%',
            background: 'var(--surface-2)',
            borderRadius: 20,
            overflow: 'hidden',
            border: '1.5px solid var(--card-border)',
            boxShadow: '0 12px 28px rgba(20, 16, 50, 0.14)',
            marginBottom: 16,
          }}
        >
          <video
            data-testid={`${testId}-preview-video`}
            src={resolveVideoSrc(value)}
            muted
            loop
            autoPlay
            playsInline
            style={{ width: '100%', height: 220, objectFit: 'cover', display: 'block' }}
          />

          <div
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '5px 10px',
              borderRadius: 999,
              background: 'rgba(20, 16, 50, 0.62)',
              color: '#FFFFFF',
              fontFamily: "'Inria Serif', serif",
              fontSize: 12,
              fontWeight: 700,
              backdropFilter: 'blur(6px)',
              letterSpacing: '0.02em',
            }}
          >
            <Sparkles size={12} strokeWidth={2.4} />
            Intro video added
          </div>

          <button
            type="button"
            onClick={handleRemove}
            data-testid={`${testId}-remove`}
            aria-label="Remove intro video"
            style={{
              position: 'absolute',
              top: 12,
              right: 12,
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'rgba(20, 16, 50, 0.72)',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backdropFilter: 'blur(6px)',
            }}
          >
            <X size={15} color="#FFFFFF" strokeWidth={2.4} />
          </button>

          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            data-testid={`${testId}-replace`}
            style={{
              position: 'absolute',
              bottom: 12,
              right: 12,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '8px 12px',
              borderRadius: 999,
              background: 'rgba(255,255,255,0.94)',
              color: 'var(--accent-purple)',
              fontFamily: "'Inria Serif', serif",
              fontSize: 13,
              fontWeight: 700,
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 6px 16px rgba(20, 16, 50, 0.24)',
            }}
          >
            <Film size={13} strokeWidth={2.4} />
            Replace
          </button>

          <input
            ref={inputRef}
            type="file"
            accept="video/mp4,video/quicktime,video/webm,video/*"
            onChange={handlePick}
            style={{ display: 'none' }}
          />
        </div>
      </>
    );
  }

  // === empty state ===
  return (
    <>
      {renderOversizedCta()}
      <button
        type="button"
        onClick={() => inputRef.current?.click()}
        data-testid={`${testId}-uploader`}
        disabled={uploading}
        style={{
          width: '100%',
          background: 'var(--surface)',
          border: '1.5px dashed var(--card-border)',
          borderRadius: 20,
          padding: '22px 18px',
          display: 'flex',
          alignItems: 'center',
          gap: 14,
          cursor: uploading ? 'progress' : 'pointer',
          textAlign: 'left',
          marginBottom: 16,
          transition: 'border-color 200ms ease, background 200ms ease',
        }}
      >
        <span
          style={{
            width: 54,
            height: 54,
            flexShrink: 0,
            borderRadius: 16,
            background: 'var(--accent-surface-soft)',
            color: 'var(--accent-purple)',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          {uploading ? (
            <Loader2 size={24} className="intro-video-uploader-spin" />
          ) : (
            <Play size={22} strokeWidth={2} fill="var(--accent-purple)" />
          )}
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              padding: '3px 8px',
              borderRadius: 999,
              background: 'var(--status-warning-surface)',
              color: 'var(--status-warning-text)',
              fontFamily: "'Inria Serif', serif",
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.04em',
              textTransform: 'uppercase',
            }}
          >
            <Sparkles size={11} strokeWidth={2.4} />
            Highly recommended
          </span>
          <span
            style={{
              display: 'block',
              margin: '8px 0 2px',
              fontFamily: "'Inria Serif', serif",
              fontSize: 15.5,
              fontWeight: 700,
              color: 'var(--text)',
            }}
          >
            Add a short intro video (max 15 sec)
          </span>
          <span
            style={{
              display: 'block',
              fontFamily: "'Inria Serif', serif",
              fontSize: 12.5,
              color: 'var(--text-3)',
              fontWeight: 500,
              lineHeight: 1.4,
            }}
          >
            {helperText || `Plays on a loop the moment a guest opens your venue. Max ${MAX_VIDEO_SIZE_LABEL}.`}
          </span>
        </span>

        <input
          ref={inputRef}
          type="file"
          accept="video/mp4,video/quicktime,video/webm,video/*"
          onChange={handlePick}
          style={{ display: 'none' }}
        />

        <style>{`
          @keyframes intro-video-uploader-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
          .intro-video-uploader-spin { animation: intro-video-uploader-spin 0.9s linear infinite; }
        `}</style>
      </button>
    </>
  );
}
